/**
 * @fileOverview See {@link ./README.md}'s "aggregate-tracker.ts" section for details.
 */

import { ContextSetter, createFanOut, PayloadHandlers } from '../helpers/fan-out';
import { Events, AnalyticsContext } from '../events';
import { watchedFlags } from '../constants/feature-flags';
import { createConsoleLogTracker } from './console-log-tracker';
import { createInternalGTMTracker } from './internal-gtm-tracker';
import { createSiftTracker } from './sift-tracker';
import { createTelemetryTracker } from './telemetry-tracker';
import { createLDTracker } from './launchdarkly-tracker';
import { createDispensaryGTMTracker } from './dispensary-gtm-tracker';

/**
 * Add configuration details for trackers here.
 */
export type AggregateTrackerConfig = {
  /* FLOW(add-a-tracker).STEP_3a: add your tracker's configuration here */
  internalGTMEnabled: boolean;
  siftBeaconKey: string;
  siftEnabled: boolean;
};

// prettier-ignore
/**
 * Creates an object that looks like:
 * {
 *   addedProductToCart: (payload: Events['addedProductToCart'], context: AnalyticsContext) => void;
 *   placedOrder: (payload: Events['placedOrder'], context: AnalyticsContext) => void;
 *   ...
 *
 *   // to set the context passed to all event handlers
 *   setContext: (context: AnalyticsContext) => void
 * }
 */
export const createAggregateTracker = (
  config?: AggregateTrackerConfig
): ContextSetter<AnalyticsContext> & PayloadHandlers<Events, void> =>
  createFanOut<Events, AnalyticsContext>(
    (fanOut) => ({
      /**
       * Wondering what `fanOut` does? It merely converts
       *   fanOut('foo')
       * to
       *   (payload: Events['foo']) => trackers.forEach((t) => t.foo(payload, context))
       */

      /*
       * FLOW(track-an-event).STEP_2: add your event here (and [STEP_3] in every other *-tracker.ts file in this folder)
       */
      accountLogin: fanOut('accountLogin'),
      addedProductToCart: fanOut('addedProductToCart'),
      buyItAgainImpression: fanOut('buyItAgainImpression'),
      buyItAgainViewAllClicked: fanOut('buyItAgainViewAllClicked'),
      cartUpdated: fanOut('cartUpdated'),
      cartViewed: fanOut('cartViewed'),
      checkoutStarted: fanOut('checkoutStarted'),
      dutchiePayCTAButtonClicked: fanOut('dutchiePayCTAButtonClicked'),
      dutchiePayRelinkButtonViewed: fanOut('dutchiePayRelinkButtonViewed'),
      dutchiePayRelinkButtonClicked: fanOut('dutchiePayRelinkButtonClicked'),
      dutchiePayUnlinkRemoveCtaClicked: fanOut('dutchiePayUnlinkRemoveCtaClicked'),
      dutchiePayUnlinkModalViewed: fanOut('dutchiePayUnlinkModalViewed'),
      dutchiePayUnlinkConfirmed: fanOut('dutchiePayUnlinkConfirmed'),
      dutchiePayUnlinkCancelled: fanOut('dutchiePayUnlinkCancelled'),
      dutchiePayUnlinkSuccess: fanOut('dutchiePayUnlinkSuccess'),
      dutchiePayUnlinkError: fanOut('dutchiePayUnlinkError'),
      dutchiePayEnrollmentButtonClicked: fanOut('dutchiePayEnrollmentButtonClicked'),
      dutchiePayEnrollmentStep: fanOut('dutchiePayEnrollmentStep'),
      dutchiePayEnrollmentStepCancelled: fanOut('dutchiePayEnrollmentStepCancelled'),
      dutchiePayInstoreAccountLinkViewed: fanOut('dutchiePayInstoreAccountLinkViewed'),
      dutchiePayInstoreCartApprovalClicked: fanOut('dutchiePayInstoreCartApprovalClicked'),
      dutchiePayInstoreCartApprovalFailed: fanOut('dutchiePayInstoreCartApprovalFailed'),
      dutchiePayInstoreCartApprovalSucceeded: fanOut('dutchiePayInstoreCartApprovalSucceeded'),
      dutchiePayInstoreCartViewed: fanOut('dutchiePayInstoreCartViewed'),
      dutchiePayInstoreLoginViewed: fanOut('dutchiePayInstoreLoginViewed'),
      dutchiePayPlaidOnEvent: fanOut('dutchiePayPlaidOnEvent'),
      dutchiePayPlaidOnLoad: fanOut('dutchiePayPlaidOnLoad'),
      dutchiePayServiceFeeShown: fanOut('dutchiePayServiceFeeShown'),
      dutchiePayPaymentMethodSelected: fanOut('dutchiePayPaymentMethodSelected'),
      dutchiePayPlaidOnSuccess: fanOut('dutchiePayPlaidOnSuccess'),
      dutchiePayPlaidOnExit: fanOut('dutchiePayPlaidOnExit'),
      dutchiePayAeroSyncOnEvent: fanOut('dutchiePayAeroSyncOnEvent'),
      dutchiePayAeroSyncOnLoad: fanOut('dutchiePayAeroSyncOnLoad'),
      dutchiePayAeroSyncOnClose: fanOut('dutchiePayAeroSyncOnClose'),
      dutchiePayAeroSyncOnSuccess: fanOut('dutchiePayAeroSyncOnSuccess'),
      dutchiePayAeroSyncOnError: fanOut('dutchiePayAeroSyncOnError'),
      dutchiePayPreAuthEvent: fanOut('dutchiePayPreAuthEvent'),
      gaGTMClickEvent: fanOut('gaGTMClickEvent'),
      imageBannerClicked: fanOut('imageBannerClicked'),
      instoreEnrollmentStepViewed: fanOut('instoreEnrollmentStepViewed'),
      instoreEnrollmentStepCompleted: fanOut('instoreEnrollmentStepCompleted'),
      instoreEnrollmentStepSkipped: fanOut('instoreEnrollmentStepSkipped'),
      loyaltySignup: fanOut('loyaltySignup'),
      medCardSignupCompleted: fanOut('medCardSignupCompleted'),
      medCardSignupFailed: fanOut('medCardSignupFailed'),
      onboardingModalStep: fanOut('onboardingModalStep'),
      onboardingFlowStarted: fanOut('onboardingFlowStarted'),
      paymentsError: fanOut('paymentsError'),
      placedOrder: fanOut('placedOrder'),
      privacyPreferenceUpdated: fanOut('privacyPreferenceUpdated'),
      productClicked: fanOut('productClicked'),
      removedProductFromCart: fanOut('removedProductFromCart'),
      searchResults: fanOut('searchResults'),
      searchedForProduct: fanOut('searchedForProduct'),
      sponsoredBannerImpression: fanOut('sponsoredBannerImpression'),
      sponsoredBannerClicked: fanOut('sponsoredBannerClicked'),
      sponsoredProductClicked: fanOut('sponsoredProductClicked'),
      sponsoredProductImpression: fanOut('sponsoredProductImpression'),
      viewedCheckout: fanOut('viewedCheckout'),
      checkoutSession: fanOut('checkoutSession'),
      viewedProduct: fanOut('viewedProduct'),
      dispensaryClicked: fanOut('dispensaryClicked'),
      dutchiePayKioskQRCodeModalViewed: fanOut('dutchiePayKioskQRCodeModalViewed'),
      viewedKioskCheckoutModal: fanOut('viewedKioskCheckoutModal'),
      loginNudge: fanOut('loginNudge'),
      loginGateShown: fanOut('loginGateShown'),
      loginEntryPointClicked: fanOut('loginEntryPointClicked'),
      roundupForCharityViewed: fanOut('roundupForCharityViewed'),
      roundupForCharityToggled: fanOut('roundupForCharityToggled'),
      walletPass: fanOut('walletPass'),
      dutchiePayLinkToPayInitiated: fanOut('dutchiePayLinkToPayInitiated'),
      dutchiePayLinkToPayIneligible: fanOut('dutchiePayLinkToPayIneligible'),
      dutchiePayEcommModalPaymentSwitchViewed: fanOut('dutchiePayEcommModalPaymentSwitchViewed'),
      dutchiePayEcommModalPaymentSwitchClicked: fanOut('dutchiePayEcommModalPaymentSwitchClicked'),
      dutchiePayEcommModalPaymentSwitchOutcome: fanOut('dutchiePayEcommModalPaymentSwitchOutcome'),
      dutchiePayLoyaltyCtaClicked: fanOut('dutchiePayLoyaltyCtaClicked'),
      productRatingViewed: fanOut('productRatingViewed'),
      productReviewsViewed: fanOut('productReviewsViewed'),
      productReviewAiSummaryViewed: fanOut('productReviewAiSummaryViewed'),
      productReviewStatusChecked: fanOut('productReviewStatusChecked'),
      productReviewCtaClicked: fanOut('productReviewCtaClicked'),
      productReviewSubmitted: fanOut('productReviewSubmitted'),
      conciergeEntryPointClicked: fanOut('conciergeEntryPointClicked'),
      conciergeImpression: fanOut('conciergeImpression'),
      conciergeAction: fanOut('conciergeAction'),
      conciergeMessageSent: fanOut('conciergeMessageSent'),
      conciergeExited: fanOut('conciergeExited'),
      conciergeBundleAdded: fanOut('conciergeBundleAdded'),
      conciergeAccessGate: fanOut('conciergeAccessGate'),
      conciergeConversationRefreshed: fanOut('conciergeConversationRefreshed'),
      conciergeMessageStopped: fanOut('conciergeMessageStopped'),
      conciergeError: fanOut('conciergeError'),
    }),
    {
      activeNavigationBreadcrumbs: null,
      activeMenuVariant: null,
      activeProductPosition: null,
      activeCart: null,
      activeDispensary: null,
      activeOrder: null,
      activePath: null,
      activeUser: null,
      activeIsUserEnrolledDutchiePay: null,
      activePromotion: null,
      cartDiscountSummary: null,
      checkoutToken: null,
      dutchiePaySessionToken: null,
      featureFlags: { ...watchedFlags },
      isKioskOrder: null,
      isHubOrder: null,
      ldClient: undefined,
    },
    config
      ? [
        /* FLOW(add-a-tracker).STEP_3b: create your tracker here */
        createConsoleLogTracker(),
        createSiftTracker({ beaconKey: config.siftBeaconKey, enabled: config.siftEnabled }),
        createInternalGTMTracker({ enabled: config.internalGTMEnabled }),
        createTelemetryTracker(),
        createLDTracker(),
        createDispensaryGTMTracker(),
      ]
      : []
  );
